import { get, send } from "../api.js";

export type RecurrenceStatus = "active" | "paused" | "cancelled" | "completed";
export type RecurrenceCadence = "weekly" | "fortnightly";
export type RecurrenceFundingMode = "credit_then_pending";
export type RecurrenceFundingState = "credit_funded" | "payment_pending";

export interface RecurrenceListPageParams {
  limit: number;
  offset: number;
}

export interface RecurrencePlanSummary {
  id: string;
  idempotency_key?: string;
  portal_student_id: string | null;
  student_name: string | null;
  provider_id: number;
  service_id: number;
  funding_mode: RecurrenceFundingMode;
  starts_on: string;
  start_time: string;
  ends_on: string | null;
  cadence: RecurrenceCadence;
  status: RecurrenceStatus;
  persons: number;
  appointment_status: string;
  internal_notes: string;
  notify_participants: boolean;
  /** Timestamp used as a settings-save precondition. */
  updated_at: string;
}

export interface RecurrenceOccurrence {
  id: string;
  starts_at: string;
  status: string;
  amelia_appointment_id: number | null;
  funding_state: RecurrenceFundingState | null;
  lesson_credit_transaction_id: string | null;
  amelia_payment_id: number | null;
  failure_reason: string | null;
  attempt_count: number;
}

export interface RecurrencePlanDetail {
  plan: RecurrencePlanSummary;
  occurrences: RecurrenceOccurrence[];
  occurrence_count: number;
  pending_count: number;
  booked_count: number;
  unavailable_count: number;
  failed_count: number;
  cancelled_count: number;
}

export interface RecurrencePlanCreateInput {
  portal_student_id: string;
  provider_id: number;
  service_id: number;
  funding_mode: RecurrenceFundingMode;
  starts_at: string;
  cadence: RecurrenceCadence;
  occurrence_count: number;
  continue_indefinitely: boolean;
  persons: number;
  appointment_status: "approved" | "pending";
  internal_notes: string;
  notify_participants: boolean;
  idempotency_key: string;
}

export interface RecurrencePlanSettingsInput {
  notify_participants: boolean;
  internal_notes: string;
  expected_updated_at?: string;
}

export interface RecurrenceBookingResponse {
  plan: RecurrencePlanSummary;
  plan_created: boolean;
  occurrences: RecurrenceOccurrence[];
  requested_count: number;
  pending_count: number;
  booked_count: number;
  unavailable_count: number;
  failed_count: number;
}

export interface RecurrenceFundingSummary {
  creditFunded: number;
  paymentPending: number;
  notFundedYet: number;
}

/**
 * Summarise only the funding decisions returned by WMF-SIte.
 *
 * This deliberately does not inspect local credit balances or infer what a
 * future occurrence ought to do. The browser reports the backend decision for
 * each occurrence after creation; availability/funding policy stays server-side.
 */
export function recurrenceFundingSummary(
  occurrences: readonly RecurrenceOccurrence[],
): RecurrenceFundingSummary {
  return occurrences.reduce<RecurrenceFundingSummary>(
    (summary, occurrence) => {
      if (occurrence.funding_state === "credit_funded") summary.creditFunded += 1;
      else if (occurrence.funding_state === "payment_pending") summary.paymentPending += 1;
      else summary.notFundedYet += 1;
      return summary;
    },
    { creditFunded: 0, paymentPending: 0, notFundedYet: 0 },
  );
}

/** Recurrence creation and lifecycle calls from WMF-SIte's staff contract. */
export const recurringLessons = {
  create: (input: RecurrencePlanCreateInput) =>
    send<RecurrenceBookingResponse>(
      "POST",
      "/staff-api/api/v1/recurrences",
      input,
      { "Idempotency-Key": input.idempotency_key },
    ),
  list: (
    status?: RecurrenceStatus,
    page?: RecurrenceListPageParams,
    providerId?: number,
  ) =>
    get<{ items: RecurrencePlanSummary[]; count: number }>("/staff-api/api/v1/recurrences", {
      ...(status ? { status } : {}),
      ...(page ? { limit: String(page.limit), offset: String(page.offset) } : {}),
      ...(providerId ? { provider_id: String(providerId) } : {}),
    }),
  detail: (planId: string) =>
    get<RecurrencePlanDetail>(`/staff-api/api/v1/recurrences/${encodeURIComponent(planId)}`),
  updateSettings: (planId: string, input: RecurrencePlanSettingsInput) =>
    send<RecurrencePlanDetail>(
      "PATCH",
      `/staff-api/api/v1/recurrences/${encodeURIComponent(planId)}`,
      input,
    ),
  pause: (planId: string) =>
    send<RecurrencePlanDetail>(
      "PATCH",
      `/staff-api/api/v1/recurrences/${encodeURIComponent(planId)}/status`,
      { status: "paused" },
    ),
  resume: (planId: string) =>
    send<RecurrencePlanDetail>(
      "PATCH",
      `/staff-api/api/v1/recurrences/${encodeURIComponent(planId)}/status`,
      { status: "active" },
    ),
  stop: (planId: string) =>
    send<RecurrencePlanDetail>(
      "PATCH",
      `/staff-api/api/v1/recurrences/${encodeURIComponent(planId)}/status`,
      { status: "cancelled" },
    ),
  /** With `occurrenceIds`, only those occurrences are retried; without, every retryable one is. */
  retry: (planId: string, occurrenceIds?: readonly string[]) =>
    send<RecurrencePlanDetail>(
      "POST",
      `/staff-api/api/v1/recurrences/${encodeURIComponent(planId)}/retry`,
      occurrenceIds === undefined ? {} : { occurrence_ids: occurrenceIds },
    ),
};
